/**
 * Singleton modal with Leaflet map for picking a point. No area markers.
 * On map click: reverse-geocodes for address, finds nearest areas for IDs;
 * on confirm calls onSelect({ lat, lng, displayAddress }).
 *
 * API: open({ locations, onSelect [, currentLocation] })
 * Leaflet (L) is assumed to be loaded globally by the layout.
 */

import { forwardGeocode, reverseGeocode, isMapLink, resolveMapLink } from './geocoder';

const CONTAINER_ID = 'location-map-modal';
const MAP_PANE_ID = 'location-map-modal-map';
const MAP_PANE_WIDTH = 640;
const MAP_PANE_HEIGHT = 360;
const DEFAULT_CENTER = [25.0, 45.0];
const DEFAULT_ZOOM = 3;
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/**
 * Run callback when the map pane has non-zero dimensions (so Leaflet renders correctly on first open).
 * Polls with rAF until size is ready or timeout; then runs callback exactly once.
 */
function whenMapPaneReady(pane, callback, timeoutMs = 1500) {
  if (!pane) {
    callback();
    return;
  }
  const hasSize = () => pane.offsetWidth > 0 && pane.offsetHeight > 0;
  if (hasSize()) {
    callback();
    return;
  }
  let done = false;
  const runOnce = () => {
    if (done) return;
    done = true;
    callback();
  };
  const start = Date.now();
  const tryReady = () => {
    if (done) return;
    if (hasSize() || Date.now() - start >= timeoutMs) {
      runOnce();
      return;
    }
    requestAnimationFrame(tryReady);
  };
  requestAnimationFrame(tryReady);
}

function hasCoords(loc) {
  return (
    loc &&
    typeof loc.lat === 'number' &&
    typeof loc.lng === 'number' &&
    Number.isFinite(loc.lat) &&
    Number.isFinite(loc.lng)
  );
}

let instance = null;

/**
 * Ensure the modal DOM exists. Creates it if missing (so the module works
 * even before the layout adds #location-map-modal).
 * @returns {HTMLElement} Root element of the modal
 */
function getOrCreateContainer() {
  let root = document.getElementById(CONTAINER_ID);
  if (root) return root;

  root = document.createElement('div');
  root.id = CONTAINER_ID;
  root.setAttribute('aria-hidden', 'true');
  root.className = 'location-map-modal';
  root.innerHTML = `
    <div class="location-map-modal__backdrop" data-action="close"></div>
    <div class="location-map-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="location-map-modal-title">
      <div class="location-map-modal__header">
        <h2 id="location-map-modal-title" class="location-map-modal__title">Pick location from map</h2>
        <button type="button" class="location-map-modal__close" data-action="close" aria-label="Close">&times;</button>
      </div>
      <div class="location-map-modal__body">
        <p class="location-map-modal__hint">Enter an address, paste a Google/Apple Maps link, or click on the map. Then click <strong>Confirm</strong> to use it.</p>
        <div class="location-map-modal__address-row">
          <input type="text" class="location-map-modal__address-input" data-address-input placeholder="Address or Google/Apple Maps link" autocomplete="off" />
          <button type="button" class="location-map-modal__btn location-map-modal__btn--secondary" data-action="search-address">Search</button>
        </div>
        <p class="location-map-modal__no-location" data-no-location-msg role="alert" hidden>
          We couldn't find that location. Try using the format <strong>City, Country</strong> (e.g. "Alexandria, Egypt") and search again.
        </p>
        <div id="${MAP_PANE_ID}" class="location-map-modal__map" style="width: ${MAP_PANE_WIDTH}px; height: ${MAP_PANE_HEIGHT}px;"></div>
      </div>
      <div class="location-map-modal__footer">
        <button type="button" class="location-map-modal__btn location-map-modal__btn--secondary" data-action="close">Cancel</button>
        <button type="button" class="location-map-modal__btn location-map-modal__btn--primary" data-action="confirm" data-confirm-btn hidden>Confirm</button>
      </div>
    </div>
  `;
  document.body.appendChild(root);
  return root;
}

/**
 * Singleton that manages the shared map modal.
 */
class LocationMapModal {
  constructor() {
    this._root = null;
    this._map = null;
    this._locationMarkers = [];
    this._pickMarker = null;
    this._pickPopup = null;
    this._currentLocationMarker = null;
    this._currentLocation = null;
    this._locations = [];
    this._onSelect = null;
    this._pendingPick = null;
    this._mapClickHandler = null;
    this._returnFocusTo = null;
    this._addressInputKeyHandler = null;
    this._boundClose = () => this.close();
    this._boundConfirm = () => this._confirmPick();
    this._boundSearchAddress = () => this._onSearchAddress();
  }

  _getAddressInput() {
    return this._root?.querySelector('[data-address-input]');
  }

  /**
   * Open the modal with the given locations and callback.
   * @param {Object} options
   * @param {Array<{ lat?: number, lng?: number, port?: boolean, [key: string]: unknown }>} options.locations - Dataset from selectors (only areas are shown and used for nearest)
   * @param {function(object): void} options.onSelect - Called with the selected area when user confirms
   * @param {{ lat?: number, lng?: number, [key: string]: unknown }|null} [options.currentLocation] - Currently selected area to center the map on and show as "current selection"
   */
  open({ locations = [], onSelect, currentLocation = null } = {}) {
    this._returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this._root = getOrCreateContainer();
    this._locations = Array.isArray(locations) ? locations : [];
    this._onSelect = typeof onSelect === 'function' ? onSelect : null;
    this._currentLocation = currentLocation && hasCoords(currentLocation) ? currentLocation : null;

    this._hideConfirmAction();
    this._pendingPick = null;
    const addrInput = this._root?.querySelector('[data-address-input]');
    if (addrInput) {
      addrInput.value = this._currentLocation?.displayAddress
        || this._currentLocation?.display_name
        || '';
    }
    this._bindEvents();
    this._show();

    // No area markers. Create map, set view, then show modal.
    const runAfterLayout = () => {
      if (!this._map) this._renderMap();
      if (!this._map) return;
      this._attachMapClick();
      this._fitBoundsOrCenter();
      if (this._currentLocation && hasCoords(this._currentLocation)) {
        this._renderCurrentLocationMarker();
        // Don't set _pendingPick or show Confirm on reopen — user must change location to confirm
      }
      this._map.invalidateSize();
      this._root.classList.add('location-map-modal--visible');
    };

    const pane = document.getElementById(MAP_PANE_ID);
    requestAnimationFrame(() => {
      whenMapPaneReady(pane, runAfterLayout);
    });
  }

  /** Close the modal without calling onSelect. */
  close() {
    this._unbindEvents();
    this._detachMapClick();
    this._hideConfirmAction();
    this._pendingPick = null;
    this._clearLocationMarkers();
    this._currentLocationMarker = null;
    if (this._map) {
      this._map.remove();
      this._map = null;
    }
    if (this._root) {
      if (this._root.contains(document.activeElement)) {
        if (this._returnFocusTo && this._returnFocusTo.isConnected) {
          this._returnFocusTo.focus();
        } else {
          document.body.focus();
        }
      }
      this._returnFocusTo = null;
      this._root.classList.remove('location-map-modal--open', 'location-map-modal--visible');
      this._root.setAttribute('aria-hidden', 'true');
    }
  }

  _show() {
    this._root.classList.remove('location-map-modal--visible');
    this._root.classList.add('location-map-modal--open');
    this._root.setAttribute('aria-hidden', 'false');
  }

  _renderMap() {
    const pane = document.getElementById(MAP_PANE_ID);
    if (!pane) return;

    if (this._map) {
      this._clearLocationMarkers();
      this._clearPickMarker();
      return;
    }

    if (typeof L === 'undefined') {
      console.error('LocationMapModal: Leaflet (L) is not loaded.');
      return;
    }

    // Force explicit dimensions so Leaflet always gets non-zero size (fixes markers not showing on first open).
    if (pane.offsetWidth === 0 || pane.offsetHeight === 0) {
      pane.style.width = `${MAP_PANE_WIDTH}px`;
      pane.style.height = `${MAP_PANE_HEIGHT}px`;
    }

    this._map = L.map(MAP_PANE_ID, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
    });
    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(this._map);
  }

  _clearLocationMarkers() {
    for (const m of this._locationMarkers) {
      if (this._map && m.remove) m.remove();
    }
    this._locationMarkers = [];
  }

  _clearPickMarker() {
    if (this._pickMarker && this._map) {
      this._pickMarker.remove();
      this._pickMarker = null;
    }
    if (this._pickPopup && this._map) {
      this._map.removeLayer(this._pickPopup);
      this._pickPopup = null;
    }
  }

  _clearCurrentLocationMarker() {
    if (this._currentLocationMarker && this._map) {
      this._currentLocationMarker.remove();
      this._currentLocationMarker = null;
    }
  }

  _renderCurrentLocationMarker() {
    if (!this._map || !this._currentLocation || !hasCoords(this._currentLocation)) return;
    this._clearCurrentLocationMarker();
    const { lat, lng } = this._currentLocation;
    this._currentLocationMarker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'location-map-modal__current-marker',
        html: '<span></span>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      }),
    }).addTo(this._map);
  }

  _fitBoundsOrCenter() {
    if (!this._map) return;
    if (this._currentLocation && hasCoords(this._currentLocation)) {
      this._map.setView([this._currentLocation.lat, this._currentLocation.lng], DEFAULT_ZOOM);
      return;
    }
    this._map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  }

  _attachMapClick() {
    if (!this._map) return;
    this._mapClickHandler = (e) => this._onMapClick(e);
    this._map.on('click', this._mapClickHandler);
  }

  _detachMapClick() {
    if (this._map && this._mapClickHandler) {
      this._map.off('click', this._mapClickHandler);
      this._mapClickHandler = null;
    }
  }

  _onMapClick(e) {
    this._setPickFromCoords(e.latlng.lat, e.latlng.lng);
  }

  /**
   * Set pick from coordinates: place marker, reverse geocode, find nearest areas,
   * update address input and show confirm panel.
   */
  async _setPickFromCoords(lat, lng) {
    this._clearPickMarker();
    this._clearCurrentLocationMarker();
    this._hideConfirmAction();
    this._setAddressInputValue('Getting address…');

    this._pickMarker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'location-map-modal__pick-marker',
        html: '<span></span>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      }),
    }).addTo(this._map);
    this._map.setView([lat, lng], this._map.getZoom());

    const revResult = await reverseGeocode(lat, lng);
    const displayAddress = revResult?.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

    this._pendingPick = { lat, lng, displayAddress };
    this._setAddressInputValue(displayAddress);
    this._showConfirmAction();
  }

  _setAddressInputValue(value) {
    const input = this._getAddressInput();
    if (input) input.value = value ?? '';
  }

  async _onSearchAddress() {
    const input = this._getAddressInput();
    const query = (input?.value || '').trim();
    if (!query) return;

    // A pasted Google/Apple Maps link is resolved server-side to coordinates;
    // a plain address goes through forward geocoding as before.
    if (isMapLink(query)) {
      this._setAddressInputValue('Resolving map link…');
      const link = await resolveMapLink(query);
      if (!link) {
        this._setAddressInputValue(query);
        this._showNoLocationMessage();
        return;
      }
      // Drop the pin from the coordinates only — _setPickFromCoords reverse-
      // geocodes for the displayed address, same as a manual map click.
      await this._setPickFromCoords(link.lat, link.lng);
      return;
    }

    this._setAddressInputValue('Searching…');
    const result = await forwardGeocode(query);
    if (!result) {
      this._setAddressInputValue(query);
      this._showNoLocationMessage();
      return;
    }
    await this._setPickFromCoords(result.lat, result.lng);
  }

  _showConfirmAction() {
    const noLocationMsg = this._root.querySelector('[data-no-location-msg]');
    const confirmBtn = this._root.querySelector('[data-confirm-btn]');
    if (noLocationMsg) noLocationMsg.hidden = true;
    if (confirmBtn) confirmBtn.hidden = false;
  }

  _showNoLocationMessage() {
    this._pendingPick = null;
    this._clearPickMarker();
    const confirmBtn = this._root.querySelector('[data-confirm-btn]');
    if (confirmBtn) confirmBtn.hidden = true;
    const noLocationMsg = this._root.querySelector('[data-no-location-msg]');
    if (noLocationMsg) noLocationMsg.hidden = false;
  }

  _hideConfirmAction() {
    const confirmBtn = this._root && this._root.querySelector('[data-confirm-btn]');
    if (confirmBtn) confirmBtn.hidden = true;
    const noLocationMsg = this._root && this._root.querySelector('[data-no-location-msg]');
    if (noLocationMsg) noLocationMsg.hidden = true;
  }

  _confirmPick() {
    const pick = this._pendingPick
      ? {
        lat: this._pendingPick.lat,
        lng: this._pendingPick.lng,
        displayAddress: this._pendingPick.displayAddress,
      }
      : null;
    const onSelect = this._onSelect;
    this.close();
    if (pick && typeof onSelect === 'function') {
      onSelect(pick);
    }
  }

  _bindEvents() {
    if (!this._root) return;
    this._root.querySelectorAll('[data-action="close"]').forEach((el) => {
      el.addEventListener('click', this._boundClose);
    });
    const confirmBtn = this._root.querySelector('[data-action="confirm"]');
    if (confirmBtn) confirmBtn.addEventListener('click', this._boundConfirm);
    const searchBtn = this._root.querySelector('[data-action="search-address"]');
    if (searchBtn) searchBtn.addEventListener('click', this._boundSearchAddress);
    const addressInput = this._getAddressInput();
    if (addressInput) {
      this._addressInputKeyHandler = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._onSearchAddress();
        }
      };
      addressInput.addEventListener('keydown', this._addressInputKeyHandler);
    }
  }

  _unbindEvents() {
    if (!this._root) return;
    this._root.querySelectorAll('[data-action="close"]').forEach((el) => {
      el.removeEventListener('click', this._boundClose);
    });
    const confirmBtn = this._root.querySelector('[data-action="confirm"]');
    if (confirmBtn) confirmBtn.removeEventListener('click', this._boundConfirm);
    const searchBtn = this._root.querySelector('[data-action="search-address"]');
    if (searchBtn) searchBtn.removeEventListener('click', this._boundSearchAddress);
    const addressInput = this._getAddressInput();
    if (addressInput && this._addressInputKeyHandler) {
      addressInput.removeEventListener('keydown', this._addressInputKeyHandler);
      this._addressInputKeyHandler = null;
    }
  }

  _invalidateMapSize() {
    if (this._map) {
      setTimeout(() => {
        this._map.invalidateSize();
      }, 50);
    }
  }
}

/**
 * Get the singleton instance.
 * @returns {LocationMapModal}
 */
export function getLocationMapModal() {
  if (!instance) {
    instance = new LocationMapModal();
  }
  return instance;
}

/**
 * Open the map modal. Convenience wrapper around getLocationMapModal().open().
 * @param {Object} options - Same as LocationMapModal#open
 */
export function openLocationMapModal(options) {
  getLocationMapModal().open(options);
}

export default getLocationMapModal;
